import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAuthHeaders, logout } from '../lib/auth';
import {
  BookOpen,
  Loader2,
  LogOut,
  User,
  LayoutDashboard,
  Download,
  Printer,
  FileUp,
  ArrowLeft,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import mammoth from 'mammoth';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Blue palette (matching original content generator)
const Y = {
  base:   '#c9a227',
  dark:   '#a07a18',
  light:  '#e8c040',
  bg:     '#0d1b2e',
  card:   '#1a2f4e',
  border: 'rgba(255,255,255,0.1)',
  text:   '#bfdbfe',
  dim:    'rgba(147,197,253,0.5)',
};

function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

const escHtml = (s) =>
  (s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');

export default function DevotionalGeneratorPage() {
  const navigate = useNavigate();
  const [sermonText, setSermonText] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [genProgress, setGenProgress] = useState(0);
  const [genLabel, setGenLabel] = useState('');
  const [usage, setUsage] = useState(null);
  const [limitHit, setLimitHit] = useState(null);
  const dropRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUsage(d.usage?.devotional); })
      .catch(() => {});
  }, []);

  const startProgress = () => {
    const steps = [
      { at: 0,  label: 'Reading transcript…' },
      { at: 18, label: 'Selecting scriptures…' },
      { at: 42, label: 'Writing devotionals…' },
      { at: 72, label: 'Polishing content…' },
    ];
    let val = 0;
    setGenProgress(0);
    setGenLabel(steps[0].label);
    progressRef.current = setInterval(() => {
      val += 0.7;
      const step = [...steps].reverse().find(s => val >= s.at);
      if (step) setGenLabel(step.label);
      setGenProgress(Math.min(val, 91));
      if (val >= 91) clearInterval(progressRef.current);
    }, 150);
  };

  const stopProgress = (success) => {
    clearInterval(progressRef.current);
    if (success) {
      setGenProgress(100);
      setGenLabel('Done!');
      setTimeout(() => { setGenProgress(0); setGenLabel(''); }, 700);
    } else {
      setGenProgress(0);
      setGenLabel('');
    }
  };

  const words = wordCount(sermonText);
  const tooShort = words > 0 && words < 100;

  const handleLogout = () => logout(navigate);

  // ── File extraction ──────────────────────────────────────────────────────
  const extractTextFromFile = async (file) => {
    setError('');
    setExtracting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const isDocx = file.name?.endsWith('.docx') || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isPdf  = file.type === 'application/pdf' || file.name?.endsWith('.pdf');
      if (isDocx) {
        const res = await mammoth.extractRawText({ arrayBuffer });
        setSermonText(res.value.trim());
      } else if (isPdf) {
        const pdf = await getDocument({ data: arrayBuffer }).promise;
        let text = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text += content.items.map((item) => item.str).join(' ') + '\n';
        }
        setSermonText(text.trim());
      } else {
        setError('Unsupported file type. Please drop a .docx or .pdf file.');
      }
    } catch {
      setError('Could not extract text from the file. Please paste the text manually.');
    } finally {
      setExtracting(false);
      setIsDragging(false);
    }
  };

  const handleDrop      = (e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) extractTextFromFile(f); };
  const handleDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { if (!dropRef.current?.contains(e.relatedTarget)) setIsDragging(false); };

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setError('');
    setLimitHit(null);
    if (!sermonText.trim() || words < 100) return;
    setIsGenerating(true);
    setResult(null);
    startProgress();
    try {
      const response = await fetch(`${API}/content/generate-devotional`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ sermon_text: sermonText }),
      });
      if (response.ok) {
        stopProgress(true);
        setResult(await response.json());
        // Refresh usage count
        fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setUsage(d.usage?.devotional); })
          .catch(() => {});
      } else {
        stopProgress(false);
        const err = await response.json();
        if (response.status === 429) {
          setLimitHit(err.detail);
        } else {
          setError(err.detail || 'Failed to generate. Please try again.');
        }
      }
    } catch {
      stopProgress(false);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Download TXT ────────────────────────────────────────────────────────
  const handleDownloadTxt = () => {
    if (!result) return;
    const sep = '═'.repeat(50);
    const lines = [
      `SERMON: ${result.sermonTitle || 'Untitled'}`,
      `Generated by SermonSift — sermonsift.com`,
      '',
      sep,
      '6-DAY DEVOTIONAL SERIES',
      sep,
      '',
      ...(result.dailyWords || []).flatMap((d) => [
        `${d.day.toUpperCase()} — ${d.theme}`,
        `Scripture: ${d.scripture}`,
        `"${d.scriptureText}"`,
        '',
        d.devotional,
        '',
        '─'.repeat(40),
        '',
      ]),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${(result.sermonTitle || 'devotional').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-devotional.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Print HTML ──────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!result) return;
    const w = window.open('', '_blank', 'width=900,height=1100');
    w.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${escHtml(result.sermonTitle || '6-Day Devotional')}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    @page{margin:0;size:letter}
    body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;font-size:11pt;line-height:1.7;background:#fff}
    .doc-header{background:#1B2A4A;padding:28pt 0.85in 24pt}
    .doc-header .eyebrow{font-family:Arial,sans-serif;font-size:7.5pt;letter-spacing:.15em;text-transform:uppercase;color:rgba(255,255,255,.55);margin-bottom:6pt}
    .doc-header h1{font-size:22pt;font-weight:bold;color:#fff;line-height:1.2}
    .doc-header .sub{font-family:Arial,sans-serif;font-size:9pt;color:rgba(255,255,255,.55);margin-top:5pt}
    .body{padding:28pt 0.85in 0.85in}
    .day-card{margin-bottom:24pt;page-break-inside:avoid;border:1px solid #e5e7eb;border-radius:6px;overflow:hidden}
    .day-header{background:#1B2A4A;padding:10pt 14pt;display:flex;align-items:baseline;gap:12pt}
    .day-label{font-family:Arial,sans-serif;font-size:10pt;font-weight:700;color:#d4a017;text-transform:uppercase;letter-spacing:.08em}
    .day-theme{font-family:Arial,sans-serif;font-size:8.5pt;color:rgba(255,255,255,.6);font-style:italic}
    .day-body{padding:12pt 14pt}
    .scripture-ref{font-family:Arial,sans-serif;font-size:8pt;font-weight:700;color:#d4a017;text-transform:uppercase;letter-spacing:.08em;margin-bottom:4pt}
    .scripture-text{font-style:italic;color:#444;font-size:10.5pt;line-height:1.5;margin-bottom:10pt;padding-left:10pt;border-left:3px solid #d4a017}
    .devotional{color:#1a1a1a;font-size:11pt;line-height:1.75}
    footer{margin-top:32pt;padding-top:10pt;border-top:1px solid #ddd;text-align:center;font-family:Arial,sans-serif;font-size:7.5pt;color:#bbb;letter-spacing:.05em}
  </style>
</head>
<body>
<div class="doc-header">
  <div class="eyebrow">6-Day Devotional Series</div>
  <h1>${escHtml(result.sermonTitle || 'Daily Devotionals')}</h1>
  <div class="sub">Generated by SermonSift &nbsp;&middot;&nbsp; sermonsift.com</div>
</div>
<div class="body">
  ${(result.dailyWords || []).map((d) => `
  <div class="day-card">
    <div class="day-header">
      <span class="day-label">${escHtml(d.day)}</span>
      <span class="day-theme">${escHtml(d.theme)}</span>
    </div>
    <div class="day-body">
      <div class="scripture-ref">${escHtml(d.scripture)}</div>
      <div class="scripture-text">&ldquo;${escHtml(d.scriptureText)}&rdquo;</div>
      <div class="devotional">${escHtml(d.devotional)}</div>
    </div>
  </div>`).join('')}
  <footer>Generated by SermonSift &nbsp;&middot;&nbsp; sermonsift.com</footer>
</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 600);
  };

  return (
    <div className="min-h-screen" style={{ background: '#0d1b2e' }}>
      {/* Navbar */}
      <header className="sticky top-0 z-50 border-b" style={{ background: 'rgba(13,27,46,0.95)', backdropFilter: 'blur(8px)', borderColor: Y.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-1.5 text-sm transition-colors" style={{ color: Y.dim }}>
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Dashboard</span>
            </Link>
            <span style={{ color: Y.border }}>|</span>
            <Link to="/" className="font-serif text-xl font-bold" style={{ color: '#fff' }}>SermonSift</Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 text-sm font-medium" style={{ color: Y.base }}>
              <BookOpen className="w-4 h-4" /> 6-Day Devotional
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-lg transition-colors hover:bg-white/10">
                  <User className="w-5 h-5" style={{ color: Y.dim }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Page header */}
        <div className="mb-8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${Y.base}22` }}>
            <BookOpen className="w-5 h-5" style={{ color: Y.base }} strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-xl font-serif font-bold text-white leading-none">6-Day Devotional Generator</h1>
            <p className="text-xs mt-0.5" style={{ color: Y.dim }}>
              Paste a sermon transcript → generate a Monday–Saturday devotional series
            </p>
          </div>
        </div>

        {/* Input panel */}
        <div className="rounded-2xl p-5 mb-8 border" style={{ background: '#1a2f4e', borderColor: Y.border }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: Y.text }}>Sermon Transcript</span>
            <span className="text-xs tabular-nums" style={{ color: tooShort ? '#f59e0b' : Y.dim }}>
              {words.toLocaleString()} words
            </span>
          </div>

          <div ref={dropRef} className="relative" onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
            {isDragging && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed pointer-events-none" style={{ borderColor: Y.base, background: `${Y.base}18` }}>
                <FileUp className="w-8 h-8" style={{ color: Y.base }} />
                <p className="text-sm font-semibold" style={{ color: Y.base }}>Drop to extract text</p>
                <p className="text-xs" style={{ color: Y.dim }}>.docx or .pdf</p>
              </div>
            )}
            {extracting && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-lg backdrop-blur-sm" style={{ background: 'rgba(13,27,46,0.8)' }}>
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: Y.base }} />
                <p className="text-sm" style={{ color: Y.base }}>Extracting text…</p>
              </div>
            )}
            <textarea
              value={sermonText}
              onChange={(e) => setSermonText(e.target.value)}
              placeholder="Paste the full sermon transcript here… or drop a .docx / .pdf file"
              className="w-full rounded-lg p-4 min-h-[180px] resize-y border focus:outline-none transition-colors text-sm leading-relaxed"
              style={{ background: '#0d1b2e', color: Y.text, borderColor: Y.border, caretColor: Y.base }}
            />
          </div>

          {tooShort && <p className="mt-2 text-xs text-amber-400">⚠ Paste at least 100 words for best results.</p>}
          {error   && <p className="mt-2 text-xs text-red-400">{error}</p>}

          <div className="flex justify-end mt-3">
            <button
              onClick={handleGenerate}
              disabled={isGenerating || words < 100}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: isGenerating || words < 100 ? '#2d4a6e' : `linear-gradient(135deg, ${Y.base} 0%, ${Y.light} 100%)`,
                color: isGenerating || words < 100 ? Y.dim : '#1a1200',
              }}
            >
              {isGenerating ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</> : <><BookOpen className="w-4 h-4" /> Generate Devotional</>}
            </button>
          </div>

          {/* Usage badge */}
          {usage && (
            <div className="flex items-center gap-1.5 text-xs mt-2 justify-end">
              {usage.unlimited
                ? <span style={{ color: Y.dim }}>Unlimited</span>
                : <span style={{ color: usage.remaining === 0 ? '#f87171' : usage.remaining <= 2 ? '#f59e0b' : Y.dim }}>
                    {usage.remaining} of {usage.limit} uses left this month
                  </span>
              }
            </div>
          )}

          {/* Limit alert */}
          {limitHit && (
            <div className="mt-3 rounded-xl border border-red-800 bg-red-950 p-4 text-center">
              <p className="text-sm font-semibold text-red-300 mb-1">Monthly limit reached</p>
              <p className="text-xs text-red-400 mb-3">{limitHit}</p>
              <Link to="/pricing">
                <button type="button" className="text-xs font-semibold px-4 py-2 rounded-lg bg-blue-700 text-white">
                  Upgrade Plan
                </button>
              </Link>
            </div>
          )}

          {isGenerating && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs" style={{ color: Y.dim }}>{genLabel}</span>
                <span className="text-xs font-bold tabular-nums" style={{ color: Y.base }}>{Math.round(genProgress)}%</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${genProgress}%`, background: `linear-gradient(90deg, ${Y.base}, ${Y.light})` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {result && (
          <div>
            {result.sermonTitle && (
              <p className="text-xs mb-4 text-center" style={{ color: Y.dim }}>
                Based on: <span className="italic" style={{ color: Y.text }}>"{result.sermonTitle}"</span>
              </p>
            )}

            <div className="flex gap-3 mb-5">
              <button
                onClick={handleDownloadTxt}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium border transition-colors hover:border-opacity-60"
                style={{ background: '#1a1800', color: Y.text, borderColor: Y.border }}
              >
                <Download className="w-3.5 h-3.5" /> Download TXT
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium border transition-colors hover:border-opacity-60"
                style={{ background: '#1a1800', color: Y.text, borderColor: Y.border }}
              >
                <Printer className="w-3.5 h-3.5" /> Print / Save PDF
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(result.dailyWords || []).map((d) => (
                <div key={d.day} className="rounded-xl overflow-hidden border" style={{ background: '#1a2f4e', borderColor: Y.border }}>
                  <div className="px-4 py-3 flex items-baseline gap-3 border-b" style={{ background: '#0f1200', borderColor: Y.border }}>
                    <span className="font-bold text-xs uppercase tracking-wider" style={{ color: Y.base }}>{d.day}</span>
                    <span className="text-xs italic truncate" style={{ color: Y.dim }}>{d.theme}</span>
                  </div>
                  <div className="p-4">
                    <div className="text-xs font-semibold mb-1" style={{ color: Y.base }}>{d.scripture}</div>
                    <p className="text-xs italic mb-3 leading-relaxed pl-2 border-l-2" style={{ color: Y.dim, borderColor: `${Y.base}50` }}>
                      "{d.scriptureText}"
                    </p>
                    <p className="text-sm leading-relaxed" style={{ color: Y.text }}>{d.devotional}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
